import { Injectable } from '@angular/core';
import { User } from '@auth0/auth0-spa-js';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { StatusType } from '@datadog/browser-logs/bundle/datadog-logs';
import { environment } from '@environments/environment';
import { appName, appVersion } from '@environments/shared';
import { PermissionsService } from '@app/modules/auth';
import { isLocalDevEnv } from '@app/modules/shared/utilities/utilities';
import { first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DataDogService {
  private user: User;

  constructor(private permissions: PermissionsService) {
    if (!isLocalDevEnv()) {
      // enable real user monitoring for all non-local (deployed) environments
      datadogRum.init({
        applicationId: environment.datadog.applicationId,
        clientToken: environment.datadog.clientToken,
        env: environment.datadog.tagEnvironment,
        service: appName,
        version: appVersion,
        sampleRate: 100,
        sessionSampleRate: 100,
        premiumSampleRate: 100,
        trackUserInteractions: true,
        replaySampleRate: 100,
        trackInteractions: true,
        allowedTracingOrigins: [/https:\/\/.*\.vdo-fleet\.com/],
        site: environment.datadogSite,
        useSecureSessionCookie: false,
        useCrossSiteSessionCookie: true,
        defaultPrivacyLevel: 'mask-user-input'
      });

      datadogLogs.init({
        clientToken: environment.datadog.clientToken,
        env: environment.datadog.tagEnvironment,
        service: appName,
        site: environment.datadogSite,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        forwardConsoleLogs: 'all',
        useSecureSessionCookie: false,
        useCrossSiteSessionCookie: true
      });

      this.permissions.users$.pipe(first(users => Boolean(users?.length))).subscribe(user => {
        this.user = user[0];
        datadogRum.setUser({
          id: this.user.id,
          email: this.user.email,
          sub: this.user.sub
        });
      });
    }
  }

  addRumAction(name: string, context?: object | undefined): void {
    if (!isLocalDevEnv()) {
      datadogRum.addAction(name, context);
    }
  }

  addRumError(error: Error, context?: object | undefined): void {
    if (!isLocalDevEnv()) {
      datadogRum.addError(error, context);
    }
  }

  addRumTiming(name: string, time?: number | undefined): void {
    if (!isLocalDevEnv()) {
      datadogRum.addTiming(name, time || Date.now());
    }
  }

  getRumUser(): User | undefined {
    return this.user;
  }

  startSessionReplayRecording(): void {
    datadogRum.startSessionReplayRecording();
  }

  stopSessionReplayRecording(): void {
    datadogRum.stopSessionReplayRecording();
  }

  log(message: string, attributes: object = {}): void {
    if (!isLocalDevEnv()) {
      datadogLogs.logger.info(message, attributes);
    }
  }

  logError(message: string, err: any, attributes: object = {}): void {
    if (!isLocalDevEnv()) {
      datadogLogs.logger.error(message, attributes, err);
    }
  }
}
